import _get from 'lodash/get';
import type { Event as BugsnagEvent } from '@bugsnag/js';

export const PRELOAD_ERROR = 'Something went wrong in preload';
export const MODULE_FAILED_TO_LOAD = 'Module failed to load or resolved to undefined';
const UNHANDLED_REJECTION_MESSAGE = 'unhandledrejection handler received a non-error';

// Handled internally by the API layer (400 validation, 401 auth) or not a bug at all
// (404 resource-not-found). Never reported, whether or not the event is unhandled.
const ALWAYS_SUPPRESSED_STATUS_CODES = [400, 401, 404];

// Additionally filtered out when the event is an unhandled rejection, while still allowing
// important errors like 413 (Payload Too Large) to be reported.
const INVALID_STATUS_CODES = [...ALWAYS_SUPPRESSED_STATUS_CODES, 403, 500];

export const isUnhandledAndUnauthenticatedApiError = (event: BugsnagEvent) => {
  const errorMessage = _get(event, 'errors[0].errorMessage', '').toLowerCase();
  const is_unhandled = errorMessage.includes(UNHANDLED_REJECTION_MESSAGE);
  const statusCodeFromErrors = _get(event, 'errors[0].metaData.response.status') as
    | number
    | undefined;
  const statusCodeFromOriginal = _get(event, 'originalError.status') as number | undefined;

  const statusCode = statusCodeFromErrors ?? statusCodeFromOriginal;

  const isStatusCodeInvalid =
    typeof statusCode === 'number' && INVALID_STATUS_CODES.includes(statusCode);

  const should_always_suppress =
    typeof statusCode === 'number' && ALWAYS_SUPPRESSED_STATUS_CODES.includes(statusCode);

  const is_invalid_log = is_unhandled || isStatusCodeInvalid;

  return {
    is_unhandled,
    is_invalid_log,
    status_code: statusCode,
    should_always_suppress
  };
};

const NETWORK_ERROR_MESSAGES = ['network error', 'request aborted'];
// Axios codes for the same failures: offline / DNS / CORS preflight, and an aborted
// or timed-out request.
const NETWORK_ERROR_CODES = ['ERR_NETWORK', 'ECONNABORTED'];

/**
 * Axios connectivity failures are environment issues, not bugs. The user is already told to
 * check their connection through `handleApiErrorNotification`, so these must not reach Bugsnag.
 */
export const isNetworkOrAbortedRequestError = (event: BugsnagEvent) => {
  const messages = [
    _get(event, 'errors[0].errorMessage', ''),
    _get(event, 'originalError.message', '')
  ];

  const hasNetworkErrorMessage = messages.some(
    message =>
      typeof message === 'string' &&
      NETWORK_ERROR_MESSAGES.some(networkError => message.toLowerCase().includes(networkError))
  );

  if (hasNetworkErrorMessage) return true;

  const code = _get(event, 'originalError.code');

  return typeof code === 'string' && NETWORK_ERROR_CODES.includes(code);
};

export const isLazyRetryError = (event: BugsnagEvent) => {
  const errorMessage = _get(event, 'errors[0].errorMessage', '');

  return errorMessage.includes(PRELOAD_ERROR) || errorMessage.includes(MODULE_FAILED_TO_LOAD);
};

export const isMapboxSignalAborted = (event: BugsnagEvent) => {
  const errorMessage = _get(event, 'errors[0].errorMessage', '');
  const message = _get(event, 'errors[0].message', '');

  if (
    (typeof message === 'string' && message.includes('signal is aborted')) ||
    (typeof errorMessage === 'string' && errorMessage.includes('signal is aborted'))
  ) {
    return true;
  }

  return false;
};
